import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/data/translations";
import { ArrowUpRight, MessageCircle, Mail, CalendarCheck } from "lucide-react";

const HeroActions = ({ t }) => (
    <div className="flex flex-col gap-2.5">
        <a
            href={CONTACT.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-calendly-cta"
            className="group relative inline-flex items-center justify-between gap-3 px-5 py-4 bg-white text-black font-display font-semibold text-[12px] uppercase tracking-[0.16em] rounded-sm hover:bg-fw hover:text-black hover:glow-green transition-all duration-300 overflow-hidden"
        >
            <span className="absolute left-0 top-0 bottom-0 w-1 bg-fw" aria-hidden="true" />
            <span className="flex items-center gap-2 pl-2">
                <CalendarCheck className="w-4 h-4" strokeWidth={2} />
                {t.hero.ctaConsult}
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </a>
        <a
            href={CONTACT.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="group inline-flex items-center justify-between gap-3 px-5 py-4 bg-fw text-black font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300"
        >
            <span className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" strokeWidth={2} />
                {t.hero.ctaPrimary}
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </a>
        <a
            href={CONTACT.emailUrl}
            data-testid="hero-email-cta"
            className="inline-flex items-center justify-between gap-3 px-5 py-4 border border-white/30 text-white font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
        >
            <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" strokeWidth={2} />
                {t.hero.ctaSecondary}
            </span>
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
        </a>
    </div>
);

const HeroMarquee = ({ words }) => (
    <div className="relative z-10 border-t border-white/10 overflow-hidden bg-black/40 backdrop-blur-sm">
        <div className="flex whitespace-nowrap animate-marquee py-3.5">
            {[...words, ...words, ...words, ...words].map((word, i) => (
                <div key={`${word}-${i}`} className="flex items-center gap-6 px-6">
                    <span className="font-heavy text-2xl md:text-3xl uppercase text-white/90">{word}</span>
                    <span className="w-2 h-2 rounded-full bg-fw" />
                </div>
            ))}
        </div>
    </div>
);

const HeroMetaBar = ({ t }) => (
    <div className="relative z-10 mx-auto max-w-[1440px] w-full px-5 md:px-10 lg:px-16 py-3.5 flex items-center justify-between border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
        <span>Est. 2021 — Genova, Italia</span>
        <span className="hidden md:inline">{t.hero.scroll} ↓</span>
        <span>Method 01 / 07</span>
    </div>
);

export const Hero = () => {
    const { t } = useLanguage();

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[58vh] lg:min-h-[62vh] bg-black text-white flex flex-col justify-between overflow-hidden pt-2 md:pt-3"
        >
            <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
            <div className="absolute inset-0 grain-overlay pointer-events-none" />
            <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-fw/20 blur-[160px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-fw/10 blur-[180px] pointer-events-none" />

            <div className="relative z-10 mx-auto max-w-[1440px] w-full px-5 md:px-10 lg:px-16 flex-1 flex flex-col justify-center py-4 md:py-6">
                <div className="grid grid-cols-12 gap-6 items-center">
                    <div className="col-span-12 lg:col-span-8 animate-fade-up">
                        <div className="flex items-center gap-3 mb-3 md:mb-4">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-pulse-glow absolute inline-flex h-full w-full rounded-full bg-fw" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-fw" />
                            </span>
                            <span data-testid="hero-overline" className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/70">
                                {t.hero.overline}
                            </span>
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="hero-headline font-heavy uppercase max-w-full leading-[0.95]"
                        >
                            <span className="block text-white">{t.hero.headlineLine1}</span>
                            <span className="block text-fw text-glow-green">{t.hero.headlineLine2}</span>
                            <span className="block text-white">{t.hero.headlineLine3}</span>
                        </h1>
                    </div>

                    <div
                        className="col-span-12 lg:col-span-4 lg:pl-6 flex flex-col justify-center animate-fade-up"
                        style={{ animationDelay: "180ms", opacity: 0 }}
                    >
                        <div className="mt-4 lg:mt-0 lg:max-w-xs">
                            <p data-testid="hero-subheadline" className="font-display text-base md:text-lg text-white leading-snug mb-2">
                                {t.hero.sub}
                            </p>
                            <p data-testid="hero-description" className="text-sm text-white/65 leading-relaxed mb-4">
                                {t.hero.description}
                            </p>

                            <HeroActions t={t} />
                        </div>
                    </div>
                </div>
            </div>

            <HeroMarquee words={t.hero.tickerWords} />
            <HeroMetaBar t={t} />
        </section>
    );
};
